/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/lib/durableQuickOrderKeys';
import { QRCodeSVG } from 'qrcode.react';
import { formatTime24 } from '@/utils/formatTime';
import { Clock, Lightbulb, ShoppingCart, Menu as MenuIcon, X, Plus, Minus, LogOut, LayoutDashboard, Users, CreditCard, TrendingUp, QrCode, Settings, Package, Play, Pause, AlertTriangle, Shield, BarChart3, Building2, Bell, ChevronDown, DollarSign, Wallet, Timer, Activity, Sun, Moon, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { ClubProvider } from '@/context/ClubContext';
import { CloudAccountGate } from './components/account/CloudAccountGate';
import { FeedbackProvider } from './components/feedback/FeedbackProvider';
import { SignupLink } from './components/signup/SignupStatus';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { ForgotPasswordLink } from './components/account/ForgotPasswordLink';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LocalPosBadge } from '@/components/LocalPosBadge';
import { CloudSyncStatusBadge } from '@/components/NetworkBanner';
import { LocalUpdateNotice } from '@/components/LocalUpdateNotice';
import PosActivationPage from '@/pages/PosActivationPage';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';
import { clearSession, deviceClub, rememberClub } from './lib/deviceClub';

// Code-split heavy routes (admin/QR/member portals load only when route matched)
import { BrandMark, BrandWordmark } from '@/components/brand/Logo';
const ClubOSApp = lazy(() => import('@/components/clubos/ClubOSApp').then(m => ({ default: m.ClubOSApp })));
const QRMenuPage = lazy(() => import('@/pages/QRMenuPage').then(m => ({ default: m.QRMenuPage })));
const MemberPortal = lazy(() => import('@/pages/MemberPortal').then(m => ({ default: m.MemberPortal })));

// Loader for lazy components (full-screen spinner)
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base, #0a0a0a)' }}>
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
      <p className="mt-4 text-sm" style={{ color: 'var(--text-muted, #999)' }}>Уншиж байна...</p>
    </div>
  </div>
);

// Use REACT_APP_BACKEND_URL if set, otherwise use current origin (for production)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

// ============ CLUBOS COMPONENTS ============

// Premium Stat Card Component
const StatCardPremium = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colorStyles = {
    blue: { iconBg: 'bg-[#D97706]/15', iconColor: 'text-[#D97706]', valueColor: 'text-[#D97706]' },
    green: { iconBg: 'bg-[#22C55E]/15', iconColor: 'text-[#22C55E]', valueColor: 'text-[#22C55E]' },
    cyan: { iconBg: 'bg-[#0EA5E9]/15', iconColor: 'text-[#0EA5E9]', valueColor: 'text-[#0EA5E9]' },
    yellow: { iconBg: 'bg-[#F59E0B]/15', iconColor: 'text-[#F59E0B]', valueColor: 'text-[#F59E0B]' },
  };
  const style = colorStyles[color] || colorStyles.blue;

  return (
    <div className="stat-card-premium group">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${style.iconBg}`}>
          <Icon className={`w-5 h-5 ${style.iconColor}`} />
        </div>
        {trend && (
          <span className={`text-sm ${trend > 0 ? 'text-[#22C55E]' : 'text-[#DC2626]'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-[#525252] text-sm mb-1">{title}</p>
      <p className={`text-3xl font-bold ${style.valueColor}`}>{value}</p>
      {subtitle && <p className="text-[#525252] text-xs mt-1">{subtitle}</p>}
    </div>
  );
};

// CLUBOS Header Component
const ClubOSHeader = ({ clubName, username, onLogout }) => {
  return (
    <div className="clubos-header-bar px-6 py-3 flex items-center justify-between">
      {/* Left - Logo & Club Name */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D97706] to-[#0EA5E9] flex items-center justify-center shadow-lg shadow-[#D97706]/20">
            <span className="text-[#E5E5E5] font-bold text-lg">C</span>
          </div>
          <div>
            <h1 className="text-[#E5E5E5] font-bold text-lg tracking-tight flex items-center gap-2">
              {clubName || '147 CLUB'}
            </h1>
            <p className="text-[10px] text-[#525252] tracking-wider">Powered by CLUBOS</p>
          </div>
        </div>
      </div>

      {/* Right - Notifications & Profile */}
      <div className="flex items-center gap-3">
        {/* Notification Bell */}
        <button className="notification-bell text-[#A3A3A3] hover:text-[#E5E5E5]">
          <Bell className="w-5 h-5" />
        </button>

        {/* Profile */}
        <div className="flex items-center gap-3 pl-3 border-l border-[#262626]">
          <div className="clubos-avatar">
            {username?.charAt(0).toUpperCase() || 'A'}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-[#E5E5E5]">{username || 'Admin'}</p>
            <p className="text-xs text-[#525252]">Administrator</p>
          </div>
          <Button
            onClick={onLogout}
            variant="ghost"
            size="sm"
            className="text-[#525252] hover:text-[#E5E5E5] hover:bg-[#262626]"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};


// Theme Toggle Component - 147.mn Style
const ThemeToggle = () => {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg transition-colors"
      style={{
        background: 'var(--bg-muted)',
        color: isDark ? '#f59e0b' : '#6b7280'
      }}
      title={isDark ? 'Light mode' : 'Dark mode'}
      data-testid="theme-toggle"
    >
      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
};

// Landing Page - 147.mn Style (Light & Dark)
// Windows Offline POS download — shown on the login page BEFORE login.
const LandingPage = () => {
  const navigate = useNavigate();
  const [loginMode, setLoginMode] = useState('barmen'); // 'admin' or 'barmen'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const { isDark } = useTheme();

  // If already logged in, redirect to dashboard
  React.useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  // Global keydown event listener for PIN digit inputs
  React.useEffect(() => {
    if (loginMode !== 'barmen') return;
    const handleKeyDown = (e) => {
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) {
        return;
      }
      if (e.key >= '0' && e.key <= '9') {
        handlePinDigit(e.key);
      } else if (e.key === 'Backspace') {
        setPin(prev => prev.slice(0, -1));
      } else if (e.key === 'Escape') {
        setPin('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loginMode, pin]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_role', response.data.role);
      localStorage.setItem('admin_club_id', response.data.club_id || ''); rememberClub(response.data.club_id);
      localStorage.setItem('admin_username', username);
      toast.success('Амжилттай нэвтэрлээ');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Нэвтрэх нэр эсвэл нууц үг буруу');
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async () => {
    if (pin.length !== 4) {
      toast.error('4 оронтой PIN оруулна уу');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/shifts/barmen-login`, { pin, club_id: deviceClub() });
      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_role', user.role);
        localStorage.setItem('admin_club_id', user.club_id || ''); rememberClub(user.club_id);
        localStorage.setItem('admin_username', user.username);
        toast.success(`Сайн байна уу, ${user.username}!`);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PIN буруу байна');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handlePinDigit = (digit) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      // Auto-submit when 4 digits entered
      if (newPin.length === 4) {
        setTimeout(() => {
          setLoading(true);
          axios.post(`${API}/shifts/barmen-login`, { pin: newPin, club_id: deviceClub() })
            .then(response => {
              if (response.data.success) {
                const { token, user } = response.data;
                localStorage.setItem('admin_token', token);
                localStorage.setItem('admin_role', user.role);
                localStorage.setItem('admin_club_id', user.club_id || ''); rememberClub(user.club_id);
                localStorage.setItem('admin_username', user.username);
                toast.success(`Сайн байна уу, ${user.username}!`);
                navigate('/admin/dashboard');
              }
            })
            .catch(err => {
              toast.error(err.response?.data?.detail || 'PIN буруу байна');
              setPin('');
            })
            .finally(() => setLoading(false));
        }, 200);
      }
    }
  };

  return (
    <div className="login-screen">
      {/* Brand side (hidden on phones) */}
      <aside className="login-brand" aria-hidden="true">
        <div className="login-brand-mark"><BrandMark size={44} tile /> <BrandWordmark size={24} /></div>
        <div>
          <div className="login-brand-title">Касс, ширээ, агуулах — нэг дор.</div>
          <div className="login-brand-sub">Бильярд, кафе, ресторан, караоке</div>
        </div>
        <div className="login-brand-foot">rentos.mn</div>
      </aside>

      <main className="login-main">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="max-w-md w-full space-y-6">
        {/* RENTOS Branding (phones) */}
        <div className="login-phone-brand text-center space-y-2">
          <BrandMark size={72} tile className="mx-auto" />
          <h1 className="tracking-tight" style={{ color: 'var(--text-primary)' }}><BrandWordmark size={34} /></h1>
        </div>

        {/* Login Card */}
        <div className="login-card space-y-5">
          
          {/* Login Mode Tabs */}
          <div className="flex rounded-xl overflow-hidden" style={{background: 'var(--bg-muted)', border: '1px solid var(--border-light)'}}>
            <button
              onClick={() => { setLoginMode('barmen'); setPin(''); }}
              data-testid="login-tab-barmen"
              className={`flex-1 py-3 text-sm font-semibold transition-all ${
                loginMode === 'barmen' ? 'login-tab-on' : ''
              }`}
              style={loginMode !== 'barmen' ? {color: 'var(--text-muted)'} : {}}
            >
              Бармен (PIN)
            </button>
            <button
              onClick={() => setLoginMode('admin')}
              data-testid="login-tab-admin"
              className={`flex-1 py-3 text-sm font-semibold transition-all ${
                loginMode === 'admin' ? 'login-tab-on' : ''
              }`}
              style={loginMode !== 'admin' ? {color: 'var(--text-muted)'} : {}}
            >
              Админ
            </button>
          </div>

          {loginMode === 'admin' ? (
            /* Admin Login Form */
            <form onSubmit={handleAdminLogin} className="space-y-5" data-testid="landing-login-form">
              <div className="space-y-2">
                <Label style={{color: 'var(--text-secondary)'}} className="text-sm font-medium">Нэвтрэх нэр</Label>
                <Input
                  data-testid="landing-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-147"
                  placeholder="username"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label style={{color: 'var(--text-secondary)'}} className="text-sm font-medium">Нууц үг</Label>
                <Input
                  data-testid="landing-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-147"
                  placeholder="••••••••"
                  required
                />
                <ForgotPasswordLink />
              </div>
              <Button 
                type="submit"
                disabled={loading}
                className="w-full py-6 text-base font-semibold rounded-lg disabled:opacity-50"
                style={{background: 'var(--accent-primary)', color: 'white'}}
                data-testid="landing-login-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'НЭВТРЭХ'
                )}
              </Button>
            </form>
          ) : (
            /* Barmen PIN Login */
            <div className="space-y-4 login-pin" data-testid="barmen-pin-login">
              <p className="text-center font-semibold" style={{color: 'var(--text-primary)'}}>PIN кодоо оруулна уу</p>
              {/* PIN Display */}
              <div className="flex justify-center gap-3 py-4 rounded-xl" style={{background: 'var(--bg-muted)'}}>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full transition-all ${
                      pin.length > i ? 'login-dot-on scale-110' : ''
                    }`}
                    style={pin.length <= i ? {background: 'var(--text-subtle)'} : {}}
                  />
                ))}
              </div>

              {/* Number Pad */}
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    onClick={() => handlePinDigit(num.toString())}
                    disabled={loading}
                    className="login-key text-2xl font-semibold rounded-2xl transition-all hover:opacity-80 active:scale-95"
                    style={{background: 'var(--bg-muted)', color: 'var(--text-primary)'}}
                    data-testid={`pin-btn-${num}`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => setPin('')}
                  className="login-key text-base font-medium rounded-2xl transition-all hover:opacity-80"
                  style={{background: 'var(--bg-muted)', color: 'var(--text-muted)'}}
                >
                  C
                </button>
                <button
                  onClick={() => handlePinDigit('0')}
                  disabled={loading}
                  className="login-key text-2xl font-semibold rounded-2xl transition-all hover:opacity-80 active:scale-95"
                  style={{background: 'var(--bg-muted)', color: 'var(--text-primary)'}}
                  data-testid="pin-btn-0"
                >
                  0
                </button>
                <button
                  onClick={() => setPin(prev => prev.slice(0, -1))}
                  className="login-key text-2xl font-semibold rounded-2xl transition-all hover:opacity-80"
                  style={{background: 'var(--bg-muted)', color: 'var(--text-muted)'}}
                >
                  ←
                </button>
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <div className="w-4 h-4 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm" style={{color: 'var(--text-muted)'}}>Нэвтэрч байна...</span>
                </div>
              )}
            </div>
          )}
        </div>

        <SignupLink />

        {/* Feature badges removed per user request */}
      </div>
      </main>
    </div>
  );
};

// Old table/QR links (printed before the QR menu) open the guest QR page.
const ToGuestMenu = () => {
  const { tableId, spaceId } = useParams();
  return <Navigate to={`/menu/${tableId || spaceId}`} replace />;
};

// Admin Login
const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_role', response.data.role);
      localStorage.setItem('admin_club_id', response.data.club_id || ''); rememberClub(response.data.club_id);
      localStorage.setItem('admin_username', username);
      toast.success('Login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <Card className="glass-card p-8 w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#E5E5E5]" style={{fontFamily: 'Oswald'}}>
            <span className="text-[#22C55E] glow-emerald">ADMIN</span> LOGIN
          </h1>
        </div>

        <form data-testid="admin-login-form" onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Username</Label>
            <Input
              data-testid="username-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-black/50 border-white/10 text-[#E5E5E5] text-base"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300">Password</Label>
            <Input
              data-testid="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-black/50 border-white/10 text-[#E5E5E5] text-base"
              autoComplete="current-password"
              required
            />
          </div>

          <Button data-testid="login-submit-btn" type="submit" className="w-full btn-primary py-6 text-lg">
            LOGIN
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [activeSessions, setActiveSessions] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [membersCount, setMembersCount] = useState(0);
  const [totalMemberBalance, setTotalMemberBalance] = useState(0);
  const [orders, setOrders] = useState([]);
  const [finishedSessions, setFinishedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [durationHours, setDurationHours] = useState(1);
  const [startMode, setStartMode] = useState('timed'); // 'timed' or 'untimed'
  const [isProcessing, setIsProcessing] = useState(false); // To prevent double clicks
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [paymentType, setPaymentType] = useState('customer'); // 'customer', 'member', 'cash'
  const [paymentAmount, setPaymentAmount] = useState('');
  const [memberPin, setMemberPin] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [showQPayModal, setShowQPayModal] = useState(false);
  const [qpayData, setQpayData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Session detail modal state
  const [showSessionDetail, setShowSessionDetail] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionOrders, setSessionOrders] = useState([]);
  
  // Order detail modal state
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Real-time timer state
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Shift state - REMOVED (replaced with barmen session tracking)
  const [barmenSession, setBarmenSession] = useState(null);
  
  // Shelly config modal state
  const [showShellyConfig, setShowShellyConfig] = useState(false);
  const [shellyConfigTable, setShellyConfigTable] = useState(null);
  const [shellyDeviceId, setShellyDeviceId] = useState('');
  const [shellyIp, setShellyIp] = useState('');
  const [shellyTesting, setShellyTesting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/');
      return;
    }
    // initial load happens inside useVisiblePolling below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useVisiblePolling(loadDashboard, 5000);

  // Real-time timer update
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  // Calculate elapsed time for a session
  const getElapsedTime = (session) => {
    if (!session?.start_time) return { minutes: 0, formatted: '00:00' };
    
    const startTime = new Date(session.start_time);
    const now = currentTime;
    
    let elapsedMs;
    if (session.is_paused) {
      elapsedMs = (session.elapsed_before_pause || 0) * 60 * 1000;
    } else {
      const currentElapsed = now - startTime;
      elapsedMs = currentElapsed + (session.elapsed_before_pause || 0) * 60 * 1000;
    }
    
    const totalMinutes = Math.floor(elapsedMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return {
      minutes: totalMinutes,
      formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    };
  };

  // Load session detail with orders
  const loadSessionDetail = async (table, session) => {
    try {
      const token = localStorage.getItem('admin_token');
      const ordersRes = await axios.get(`${API}/orders/session/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionDetail({ ...session, tableId: table.id, table_code: table.table_code });
      setSessionOrders(ordersRes.data || []);
      setShowSessionDetail(true);
    } catch (error) {
      console.error('Error loading session detail:', error);
      setSessionDetail({ ...session, tableId: table.id, table_code: table.table_code });
      setSessionOrders([]);
      setShowSessionDetail(true);
    }
  };

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [dashboardRes, ordersRes, sessionsRes, membersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, config),
        axios.get(`${API}/orders`, config),
        axios.get(`${API}/sessions/finished`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/members`, config)
      ]);
      
      setTables(dashboardRes.data.tables);
      setActiveSessions(dashboardRes.data.active_sessions);
      setPendingOrders(dashboardRes.data.pending_orders);
      setTotalRevenue(dashboardRes.data.total_revenue);
      setMembersCount(dashboardRes.data.members_count || 0);
      setTotalMemberBalance(dashboardRes.data.total_member_balance || 0);
      setOrders(ordersRes.data);
      setFinishedSessions(sessionsRes.data || []);
      setMembers(membersRes.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('admin_role');
    
    // For barmen, check open sessions via API
    if (role === 'barmen' && token) {
      try {
        const res = await axios.post(`${API}/shifts/barmen-logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Open bills never block logout: the shift continues and the next
        // barmen logs in to it. Only «Ээлж хаах» requires every bill closed.
        if (res.data?.work_duration) {
          toast.success(`Ажилласан: ${res.data.work_duration}`);
        }
      } catch (err) {
        // If API fails, allow logout anyway
        console.error('Logout check error:', err);
      }
    }
    
    clearSession();
    window.location.href = '/';
  };

  // Barmen session functions (replaces shift)
  const loadBarmenSession = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/shifts/barmen-session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBarmenSession(res.data);
    } catch (error) {
      console.error('Error loading barmen session:', error);
    }
  };

  // Load barmen session on mount
  useVisiblePolling(loadBarmenSession, 60000);

  const updateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/orders/${orderId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Захиалга шинэчлэгдлээ');
      loadDashboard();
    } catch (error) {
      toast.error('Алдаа гарлаа');
    }
  };

  const handleStartTable = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = startMode === 'timed' 
        ? { duration_hours: durationHours, timed: true }
        : { duration_hours: 0, timed: false };
      
      await axios.post(`${API}/tables/${selectedTable.table_number}/book`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedTable.table_code} эхэллээ${startMode === 'timed' ? ` (${durationHours} цаг)` : ''}`);
      setShowStartModal(false);
      setSelectedTable(null);
      setDurationHours(1);
      setStartMode('timed');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Алдаа гарлаа');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePauseTable = async (tableId, sessionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/sessions/${sessionId}/pause`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Түр зогсоолоо');
      loadDashboard();
    } catch (error) {
      toast.error('Алдаа гарлаа');
    }
  };

  const handleResumeTable = async (sessionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/sessions/${sessionId}/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Үргэлжлүүллээ');
      loadDashboard();
    } catch (error) {
      toast.error('Алдаа гарлаа');
    }
  };

  const handleFinishTable = async (tableId, tableCode) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const sessionRes = await axios.get(`${API}/sessions/table/${tableId}/active`, { headers: { Authorization: `Bearer ${token}` } });
      if (sessionRes.data) {
        await axios.post(`${API}/sessions/${sessionRes.data.id}/finish`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${tableCode} дууслаа - Төлбөр төлнө үү`);
        loadDashboard();
      }
    } catch (error) {
      toast.error('Алдаа гарлаа');
    } finally {
      setIsProcessing(false);
    }
  };

  const openPaymentModal = (session) => {
    setSelectedSession(session);
    setPaymentAmount(((session.total_amount || 0) - (session.paid_amount || 0)).toString());
    setPaymentType('cash');
    setMemberPin('');
    setSelectedMember(null);
    setQpayData(null);
    setShowPaymentModal(true);
  };

  const handleCustomerQPay = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const amount = parseFloat(paymentAmount);
      
      // Create QPay invoice with 5% discount
      const res = await axios.post(`${API}/qpay/create-invoice`, {
        amount: amount,
        description: `${selectedSession.table_code} - Ширээний төлбөр`,
        order_type: 'table',
        reference_id: selectedSession.id,
        is_member: false  // Non-member gets 5% discount
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setQpayData(res.data);
      setShowQPayModal(true);
      // Start auto-checking for payment
      startQPayAutoCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'QPay нэхэмжлэл үүсгэхэд алдаа гарлаа');
    }
  };

  // QPay auto-check interval ref
  const qpayCheckInterval = React.useRef(null);

  // Start auto-checking QPay payment
  const startQPayAutoCheck = () => {
    // Clear any existing interval
    if (qpayCheckInterval.current) {
      clearInterval(qpayCheckInterval.current);
    }
    // Check every 3 seconds
    qpayCheckInterval.current = setInterval(() => {
      checkQPayPayment(true);
    }, 3000);
  };

  // Stop auto-checking
  const stopQPayAutoCheck = () => {
    if (qpayCheckInterval.current) {
      clearInterval(qpayCheckInterval.current);
      qpayCheckInterval.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopQPayAutoCheck();
  }, []);

  const checkQPayPayment = async (isAuto = false) => {
    if (!qpayData?.invoice_code) return;
    
    if (!isAuto) setCheckingPayment(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/qpay/check-payment/${qpayData.invoice_code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'paid') {
        // Stop auto-checking
        stopQPayAutoCheck();
        
        // Update session payment status
        await axios.post(`${API}/sessions/${selectedSession.id}/pay`, {
          amount: qpayData.final_amount,
          payment_method: 'qpay'
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        toast.success('QPay төлбөр амжилттай хийгдлээ!');
        setShowQPayModal(false);
        setShowPaymentModal(false);
        setQpayData(null);
        loadDashboard();
      } else if (!isAuto) {
        toast.info('Төлбөр хүлээгдэж байна...');
      }
    } catch (error) {
      if (!isAuto) toast.error('Төлбөр шалгахад алдаа гарлаа');
    }
    if (!isAuto) setCheckingPayment(false);
  };

  const handlePayment = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const amount = parseFloat(paymentAmount);
      
      if (!amount || amount <= 0) {
        toast.error('Төлбөрийн дүн оруулна уу');
        return;
      }
      
      // Cash payment - direct partial payment
      if (paymentType === 'cash') {
        await axios.post(`${API}/sessions/${selectedSession.id}/pay`,
          { amount, payment_method: 'cash' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`₮${amount.toLocaleString()} бэлэн мөнгөөр төлөгдлөө`);
        setShowPaymentModal(false);
        loadDashboard();
        return;
      }
      
      if (paymentType === 'customer') {
        // Customer pays via QPay with 5% discount
        await handleCustomerQPay();
        return;
      }
      
      if (paymentType === 'member') {
        if (!selectedMember) {
          toast.error('Гишүүн сонгоно уу');
          return;
        }
        
        // Verify PIN
        const pinRes = await axios.post(`${API}/members/${selectedMember.id}/verify-pin`,
          { pin: memberPin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!pinRes.data.valid) {
          toast.error('ПИН код буруу');
          return;
        }
        
        // Check balance
        const memberBalance = selectedMember.balance + (selectedMember.bonus_balance || 0);
        if (memberBalance < amount) {
          toast.error(`Үлдэгдэл хүрэлцэхгүй байна. Үлдэгдэл: ₮${memberBalance.toLocaleString()}`);
          return;
        }
        
        // Pay with balance
        await axios.post(`${API}/sessions/${selectedSession.id}/pay`,
          { amount, payment_method: 'balance', member_id: selectedMember.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`₮${amount.toLocaleString()} төлбөр хийгдлээ`);
        setShowPaymentModal(false);
        loadDashboard();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Төлбөр хийхэд алдаа гарлаа');
    }
  };

  // Shelly configuration functions
  const openShellyConfig = (table) => {
    setShellyConfigTable(table);
    setShellyDeviceId(table.shelly_device_id || '');
    setShellyIp(table.shelly_ip || '');
    setShowShellyConfig(true);
  };

  const saveShellyConfig = async () => {
    if (!shellyConfigTable) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.patch(`${API}/tables/${shellyConfigTable.id}/shelly`, {
        shelly_device_id: shellyDeviceId,
        shelly_ip: shellyIp
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        toast.success(`${shellyConfigTable.table_code} Shelly тохиргоо хадгалагдлаа!`);
        loadDashboard();
        setShowShellyConfig(false);
      }
    } catch (error) {
      toast.error('Shelly тохиргоо хадгалахад алдаа гарлаа');
    }
  };

  const testShellyLight = async (on) => {
    if (!shellyConfigTable) return;
    
    setShellyTesting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/tables/${shellyConfigTable.id}/test-light`, {
        on: on
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        toast.success(`${shellyConfigTable.table_code} гэрэл ${on ? 'АСАВ' : 'УНТЛАА'}!`);
      } else {
        toast.error(`Алдаа: ${res.data.message}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Гэрэл тест хийхэд алдаа гарлаа');
    }
    setShellyTesting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D97706] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#525252]">Loading CLUBOS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* CLUBOS Header */}
      <ClubOSHeader 
        clubName="147 CLUB"
        username={localStorage.getItem('admin_username') || 'Admin'}
        onLogout={handleLogout}
      />

      <div className="px-6 py-6 space-y-6">
        {/* Premium Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCardPremium 
            title="Өнөөдрийн Орлого"
            value={`₮${totalRevenue.toLocaleString()}`}
            icon={DollarSign}
            color="green"
            trend={12}
          />
          <StatCardPremium 
            title="Идэвхтэй Ширээ"
            value={activeSessions}
            subtitle={`${tables.length} нийт ширээ`}
            icon={Activity}
            color="blue"
          />
          <StatCardPremium 
            title="Гишүүд"
            value={membersCount}
            subtitle={`Үлдэгдэл: ₮${totalMemberBalance.toLocaleString()}`}
            icon={Users}
            color="cyan"
          />
          <StatCardPremium 
            title="Хүлээгдэж буй"
            value={pendingOrders}
            subtitle="захиалга"
            icon={Timer}
            color="yellow"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 flex-wrap">
          <Button
            data-testid="qr-codes-btn"
            onClick={() => navigate('/admin/qrcodes')}
            variant="outline"
            className="quick-action-btn text-[#A3A3A3] hover:text-[#D97706]"
          >
            <QrCode className="w-5 h-5 mr-2" />
            QR Кодууд
          </Button>
          <Button
            data-testid="pricing-btn"
            onClick={() => navigate('/admin/pricing')}
            variant="outline"
            className="bg-white/10 border-white/10 hover:bg-white/20"
          >
            <Settings className="w-5 h-5 mr-2" />
            Үнийн Тохиргоо
          </Button>
          <Button
            data-testid="products-btn"
            onClick={() => navigate('/admin/products')}
            variant="outline"
            className="quick-action-btn text-[#A3A3A3] hover:text-[#D97706]"
          >
            <Package className="w-5 h-5 mr-2" />
            Бүтээгдэхүүн
          </Button>
          <Button
            data-testid="reports-btn"
            onClick={() => navigate('/admin/reports')}
            variant="outline"
            className="quick-action-btn text-[#D97706] border-[#D97706]/30 bg-[#D97706]/10 hover:bg-[#D97706]/20"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Тайлан
          </Button>
          {/* Show Clubs button only for super_admin */}
          {localStorage.getItem('admin_role') === 'super_admin' && (
            <Button
              data-testid="clubs-btn"
              onClick={() => navigate('/admin/clubs')}
              variant="outline"
              className="quick-action-btn text-[#A855F7] border-[#A855F7]/30 bg-[#A855F7]/10 hover:bg-[#A855F7]/20"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Клубууд
            </Button>
          )}
          <Button
            data-testid="users-btn"
            onClick={() => navigate('/admin/users')}
            variant="outline"
            className="quick-action-btn text-[#A3A3A3] hover:text-[#D97706]"
          >
            <Shield className="w-5 h-5 mr-2" />
            Хэрэглэгчид
          </Button>
          
          {/* Barmen Work Time Info */}
          {barmenSession && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#22C55E]/10 border border-[#22C55E]/30 text-[#22C55E] text-sm">
              <Clock className="w-4 h-4" />
              <span>{barmenSession.barmen_name} - {barmenSession.work_duration || '0ц 0м'}</span>
            </div>
          )}
        </div>

        {/* Tables by Category */}
        {[
          { type: 'snooker', name: 'Snooker', color: '#22C55E' },
          { type: 'pool', name: 'Pool', color: '#D97706' },
          { type: 'darts', name: 'Darts', color: '#F59E0B' },
          { type: 'lounge', name: 'Lounge', color: '#EC4899' }
        ].map(category => {
          const categoryTables = tables.filter(t => t.table_type === category.type);
          if (categoryTables.length === 0) return null;
          
          return (
            <div key={category.type} className="space-y-4">
              <div className="section-header">
                <div className="indicator" style={{ backgroundColor: category.color }}></div>
                <h2 className="text-lg font-semibold text-[#E5E5E5]">
                  {category.name}
                  <span className="text-sm text-[#525252] ml-2 font-normal">
                    ({categoryTables.filter(t => t.status === 'occupied').length}/{categoryTables.length} идэвхтэй)
                  </span>
                </h2>
              </div>
              <div data-testid={`tables-${category.type}`} className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
                {categoryTables.map(table => {
                  const elapsed = table.active_session ? getElapsedTime(table.active_session) : null;
                  
                  return (
                  <Card 
                    key={table.id}
                    data-testid={`table-${table.table_number}`}
                    className={`p-4 text-center space-y-2 cursor-pointer transition-all hover-lift relative ${
                      table.status === 'available' ? 'table-card-available' :
                      table.status === 'occupied' ? 'table-card-occupied' : 'table-card-reserved'
                    }`}
                    onClick={() => {
                      if (table.status === 'available') {
                        setSelectedTable(table);
                        setShowStartModal(true);
                      } else if (table.status === 'occupied' && table.active_session) {
                        loadSessionDetail(table, table.active_session);
                      }
                    }}
                  >
                    {/* Shelly config button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openShellyConfig(table);
                      }}
                      className={`absolute top-2 right-2 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                        table.shelly_device_id ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-[#262626] text-[#525252] hover:text-[#E5E5E5]'
                      }`}
                      title="Shelly тохиргоо"
                    >
                      <Lightbulb className="w-3.5 h-3.5" />
                    </button>
                    
                    <div className={`text-xl font-bold ${
                      table.status === 'occupied' ? 'text-[#D97706]' : 'text-[#E5E5E5]'
                    }`}>
                      {table.table_code || table.table_number}
                    </div>
                    
                    {/* Show IP address if configured */}
                    {table.shelly_ip && (
                      <div className="text-[9px] text-[#4B5563] truncate max-w-full" title={table.shelly_ip}>
                        {table.shelly_ip}
                      </div>
                    )}
                    
                    {/* Real-time timer and amount for occupied tables */}
                    {table.status === 'occupied' && table.active_session && (
                      <>
                        <div className={`timer-display text-2xl ${table.active_session.is_paused ? 'text-[#F59E0B]' : ''}`}>
                          {elapsed?.formatted}
                          {table.active_session.is_paused && <span className="text-xs ml-1">⏸</span>}
                        </div>
                        <div className="text-lg font-bold text-[#22C55E]">
                          ₮{(table.active_session.total_amount || 0).toLocaleString()}
                        </div>
                      </>
                    )}
                    
                    {table.status === 'available' && (
                      <div className="flex items-center justify-center gap-2">
                        <div className="status-dot bg-[#22C55E]"></div>
                        <span className="text-[10px] text-[#525252] uppercase tracking-wider">Available</span>
                      </div>
                    )}
                    
                    {table.status === 'occupied' && (
                      <div className="flex gap-1.5 mt-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (table.active_session?.is_paused) {
                              handleResumeTable(table.active_session.id);
                            } else {
                              handlePauseTable(table.id, table.active_session?.id);
                            }
                          }}
                          size="sm"
                          variant="outline"
                          className="flex-1 text-[10px] py-1 h-7 bg-[#F59E0B]/15 border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/25"
                        >
                          {table.active_session?.is_paused ? 'Үргэлж' : 'Түр'}
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFinishTable(table.id, table.table_code);
                          }}
                          size="sm"
                          variant="destructive"
                          className="flex-1 text-[10px] py-1 h-7 bg-[#DC2626]/15 border-[#DC2626]/30 text-[#DC2626] hover:bg-[#DC2626]/25"
                        >
                          Дуусгах
                        </Button>
                      </div>
                    )}
                  </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Session Detail Modal */}
        {showSessionDetail && sessionDetail && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="bg-[#141414] border-[#262626] p-6 max-w-lg w-full space-y-4 max-h-[80vh] overflow-y-auto rounded-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#E5E5E5]">
                  Session Details
                </h2>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowSessionDetail(false)}
                  className="text-[#525252] hover:text-[#E5E5E5] hover:bg-[#262626]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Session Info */}
              <div className="bg-[#0A0A0A] rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Ширээ:</span>
                  <span className="text-[#E5E5E5] font-bold text-lg">{sessionDetail.table_code}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Цаг:</span>
                  <span className="timer-display text-3xl">
                    {getElapsedTime(sessionDetail).formatted}
                    {sessionDetail.is_paused && <span className="text-[#F59E0B] text-sm ml-2">⏸ Түр зогссон</span>}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Эхэлсэн:</span>
                  <span className="text-[#E5E5E5]">
                    {formatTime24(sessionDetail.start_time)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Төрөл:</span>
                  <span className={`px-2 py-1 rounded text-sm ${sessionDetail.timed ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                    {sessionDetail.timed ? `${sessionDetail.duration_hours} цаг` : 'Цаггүй'}
                  </span>
                </div>
              </div>

              {/* Orders List */}
              {sessionOrders.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-[#E5E5E5]">Захиалгууд</h3>
                  <div className="bg-black/30 rounded-lg p-3 space-y-2">
                    {sessionOrders.map((order, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                        <div>
                          <span className="text-[#E5E5E5]">{order.items?.map(i => `${i.name} x${i.quantity}`).join(', ')}</span>
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                            order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                            order.status === 'preparing' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {order.status === 'delivered' ? 'Хүргэгдсэн' : order.status === 'preparing' ? 'Бэлтгэж байна' : 'Хүлээгдэж байна'}
                          </span>
                        </div>
                        <span className="text-[#22C55E] font-semibold">₮{order.total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total Summary */}
              <div className="bg-[#22C55E]/10 rounded-lg p-4 border border-[#22C55E]/30">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Ширээний төлбөр:</span>
                  <span className="text-[#E5E5E5]">₮{(sessionDetail.table_charge || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Захиалгын дүн:</span>
                  <span className="text-[#E5E5E5]">₮{sessionOrders.reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-white/20">
                  <span className="text-[#E5E5E5] font-bold">НИЙТ ДҮН:</span>
                  <span className="text-[#22C55E] font-bold text-2xl">₮{(sessionDetail.total_amount || 0).toLocaleString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowSessionDetail(false);
                    if (sessionDetail.is_paused) {
                      handleResumeTable(sessionDetail.id);
                    } else {
                      handlePauseTable(sessionDetail.tableId, sessionDetail.id);
                    }
                  }}
                  variant="outline"
                  className="flex-1 bg-[#F59E0B]/15 border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/25"
                >
                  {sessionDetail.is_paused ? 'Үргэлжлүүлэх' : 'Түр зогсоох'}
                </Button>
                <Button
                  onClick={() => {
                    setShowSessionDetail(false);
                    handleFinishTable(sessionDetail.tableId, sessionDetail.table_code);
                  }}
                  className="flex-1 bg-[#DC2626] hover:bg-[#DC2626] text-[#E5E5E5]"
                >
                  Дуусгах
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Start Table Modal */}
        {showStartModal && selectedTable && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="bg-[#141414] border-[#262626] p-8 max-w-md w-full space-y-6 rounded-2xl">
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-[#E5E5E5] text-center">
                  Start {selectedTable.table_code}
                </h2>
                <p className="text-[#525252] text-center text-sm">Select session type</p>
              </div>
              
              {/* Mode Selection */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setStartMode('timed')}
                  variant="outline"
                  className={`py-8 rounded-xl border-2 transition-all ${startMode === 'timed' ? 'bg-[#D97706]/15 border-[#D97706] text-[#D97706]' : 'bg-[#0A0A0A] border-[#262626] text-[#525252] hover:border-[#333333]'}`}
                >
                  <Clock className="w-5 h-5 mr-2" />
                  Timed
                </Button>
                <Button
                  onClick={() => setStartMode('untimed')}
                  variant="outline"
                  className={`py-8 rounded-xl border-2 transition-all ${startMode === 'untimed' ? 'bg-[#D97706]/15 border-[#D97706] text-[#D97706]' : 'bg-[#0A0A0A] border-[#262626] text-[#525252] hover:border-[#333333]'}`}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Open
                </Button>
              </div>
              
              {startMode === 'timed' && (
                <div className="space-y-4">
                  <Label className="text-[#A3A3A3] text-sm">Duration (hours)</Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setDurationHours(Math.max(1, durationHours - 1))}
                      className="w-12 h-12 rounded-xl bg-[#0A0A0A] border-[#262626] hover:bg-[#262626] text-[#E5E5E5]"
                    >
                      <Minus className="w-5 h-5" />
                    </Button>
                    <div className="flex-1 text-center">
                      <div className="text-5xl font-bold text-[#D97706]">{durationHours}</div>
                      <div className="text-sm text-[#525252]">HOURS</div>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setDurationHours(durationHours + 1)}
                      className="w-12 h-12 rounded-xl bg-[#0A0A0A] border-[#262626] hover:bg-[#262626] text-[#E5E5E5]"
                    >
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              )}
              
              {startMode === 'untimed' && (
                <div className="text-center py-6 bg-[#0A0A0A] rounded-xl">
                  <p className="text-[#A3A3A3]">Open-ended session</p>
                  <p className="text-sm text-[#525252]">Timer will run until manually stopped</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowStartModal(false);
                    setSelectedTable(null);
                    setDurationHours(1);
                    setStartMode('timed');
                  }}
                  variant="outline"
                  className="flex-1 bg-[#0A0A0A] border-[#262626] hover:bg-[#262626] text-[#A3A3A3]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStartTable}
                  disabled={isProcessing}
                  className="flex-1 bg-[#D97706] hover:bg-[#4DA3FF] text-[#E5E5E5] py-6 text-lg shadow-lg shadow-[#D97706]/20 disabled:opacity-50"
                >
                  <Play className="w-5 h-5 mr-2" />
                  {isProcessing ? 'Уншиж байна...' : 'Start Session'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedSession && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="bg-[#141414] border-[#262626] p-6 max-w-lg w-full space-y-6 max-h-[90vh] overflow-y-auto rounded-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#E5E5E5]">
                  Payment
                </h2>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowPaymentModal(false)}
                  className="text-[#525252] hover:text-[#E5E5E5] hover:bg-[#262626]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Session Info */}
              <div className="bg-[#0A0A0A] rounded-xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#525252]">Table:</span>
                  <span className="text-[#E5E5E5] font-bold">{selectedSession.table_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#525252]">Total:</span>
                  <span className="text-[#E5E5E5] font-bold">₮{(selectedSession.total_amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#525252]">Paid:</span>
                  <span className="text-[#22C55E]">₮{(selectedSession.paid_amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t border-[#262626] pt-2 mt-2">
                  <span className="text-[#525252]">Remaining:</span>
                  <span className="text-[#D97706] font-bold text-xl">
                    ₮{((selectedSession.total_amount || 0) - (selectedSession.paid_amount || 0)).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Payment Type Selection */}
              <div className="grid grid-cols-3 gap-2">
                <Button
                  onClick={() => { setPaymentType('cash'); setSelectedMember(null); }}
                  variant="outline"
                  className={`py-4 flex flex-col items-center rounded-xl transition-all ${paymentType === 'cash' ? 'bg-[#D97706]/15 border-[#D97706] text-[#D97706]' : 'bg-[#0A0A0A] border-[#262626] text-[#525252] hover:border-[#333333]'}`}
                >
                  <span className="text-sm font-medium">Cash</span>
                </Button>
                <Button
                  onClick={() => { setPaymentType('customer'); setSelectedMember(null); }}
                  variant="outline"
                  className={`py-4 flex flex-col items-center rounded-xl transition-all ${paymentType === 'customer' ? 'bg-[#D97706]/15 border-[#D97706] text-[#D97706]' : 'bg-[#0A0A0A] border-[#262626] text-[#525252] hover:border-[#333333]'}`}
                >
                  <span className="text-sm font-medium">QPay</span>
                  <span className="text-[10px] opacity-70">-5%</span>
                </Button>
                <Button
                  onClick={() => setPaymentType('member')}
                  variant="outline"
                  className={`py-4 flex flex-col items-center rounded-xl transition-all ${paymentType === 'member' ? 'bg-[#D97706]/15 border-[#D97706] text-[#D97706]' : 'bg-[#0A0A0A] border-[#262626] text-[#525252] hover:border-[#333333]'}`}
                >
                  <span className="text-sm font-medium">Member</span>
                </Button>
              </div>

              {/* Cash Payment Info */}
              {paymentType === 'cash' && (
                <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-[#22C55E] mb-2">
                    <CreditCard className="w-5 h-5" />
                    <span className="font-semibold">Cash Payment</span>
                  </div>
                  <p className="text-sm text-[#525252]">
                    Pay full amount or partial payment in cash.
                  </p>
                </div>
              )}

              {/* Customer QPay Info */}
              {paymentType === 'customer' && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-2">
                    <CreditCard className="w-5 h-5" />
                    <span className="font-semibold">QPay төлбөр</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Үйлчлүүлэгч QPay-аар төлөхөд <span className="text-[#22C55E] font-bold">5% хөнгөлөлт</span> эдэлнэ.
                  </p>
                  <div className="mt-2 text-sm">
                    <span className="text-gray-400">Хөнгөлөлттэй дүн: </span>
                    <span className="text-[#22C55E] font-bold">
                      ₮{Math.round(parseFloat(paymentAmount || 0) * 0.95).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Member Selection */}
              {paymentType === 'member' && (
                <div className="space-y-4">
                  <Label className="text-gray-300">Гишүүн сонгох</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {members.map(member => (
                      <Button
                        key={member.id}
                        onClick={() => setSelectedMember(member)}
                        variant="outline"
                        className={`justify-start text-left h-auto py-2 ${
                          selectedMember?.id === member.id 
                            ? 'bg-[#22C55E]/20 border-[#22C55E]' 
                            : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div>
                          <div className="font-semibold text-[#E5E5E5]">{member.name}</div>
                          <div className="text-xs text-gray-400">
                            ₮{(member.balance + (member.bonus_balance || 0)).toLocaleString()}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                  
                  {selectedMember && (
                    <div className="space-y-2">
                      <Label className="text-gray-300">ПИН код</Label>
                      <Input
                        type="password"
                        value={memberPin}
                        onChange={(e) => setMemberPin(e.target.value)}
                        className="bg-black/50 border-white/10 text-[#E5E5E5] text-center text-2xl tracking-widest"
                        placeholder="****"
                        maxLength={4}
                      />
                      {selectedMember.balance + (selectedMember.bonus_balance || 0) < parseFloat(paymentAmount || 0) && (
                        <div className="text-red-500 text-sm flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Үлдэгдэл хүрэлцэхгүй байна. 
                          <Button
                            onClick={() => navigate('/admin/members')}
                            variant="link"
                            className="text-[#22C55E] p-0 h-auto"
                          >
                            Цэнэглэх
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Amount */}
              <div className="space-y-2">
                <Label className="text-gray-300">Төлөх дүн</Label>
                <Input
                  data-testid="payment-amount-input"
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="bg-black/50 border-white/10 text-[#E5E5E5] text-center text-2xl"
                  placeholder="Дурын дүн оруулах..."
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="pay-full-btn"
                    onClick={() => setPaymentAmount(((selectedSession.total_amount || 0) - (selectedSession.paid_amount || 0)).toString())}
                    variant="outline"
                    size="sm"
                    className="bg-white/5 border-white/10 text-xs"
                  >
                    Бүтнээр
                  </Button>
                  <Button
                    data-testid="pay-half-btn"
                    onClick={() => setPaymentAmount((((selectedSession.total_amount || 0) - (selectedSession.paid_amount || 0)) / 2).toString())}
                    variant="outline"
                    size="sm"
                    className="bg-white/5 border-white/10 text-xs"
                  >
                    Хагас
                  </Button>
                  <Button
                    data-testid="pay-10k-btn"
                    onClick={() => setPaymentAmount('10000')}
                    variant="outline"
                    size="sm"
                    className="bg-white/5 border-white/10 text-xs"
                  >
                    ₮10,000
                  </Button>
                  <Button
                    data-testid="pay-20k-btn"
                    onClick={() => setPaymentAmount('20000')}
                    variant="outline"
                    size="sm"
                    className="bg-white/5 border-white/10 text-xs"
                  >
                    ₮20,000
                  </Button>
                  <Button
                    data-testid="pay-50k-btn"
                    onClick={() => setPaymentAmount('50000')}
                    variant="outline"
                    size="sm"
                    className="bg-white/5 border-white/10 text-xs"
                  >
                    ₮50,000
                  </Button>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                className="w-full btn-primary py-6 text-lg"
                disabled={(paymentType !== 'customer' && paymentType !== 'cash' && !selectedMember)}
              >
                {paymentType === 'customer' ? (
                  <>QPay төлбөр үүсгэх - ₮{Math.round(parseFloat(paymentAmount || 0) * 0.95).toLocaleString()}</>
                ) : paymentType === 'cash' ? (
                  <>₮{parseFloat(paymentAmount || 0).toLocaleString()} Бэлнээр төлөх</>
                ) : (
                  <>₮{parseFloat(paymentAmount || 0).toLocaleString()} Данснаас төлөх</>
                )}
              </Button>
            </Card>
          </div>
        )}

        {/* QPay Modal */}
        {showQPayModal && qpayData && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="glass-card p-6 max-w-md w-full space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#E5E5E5]" style={{fontFamily: 'Oswald'}}>
                  QPay ТӨЛБӨР
                </h2>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    stopQPayAutoCheck();
                    setShowQPayModal(false);
                  }}
                  className="text-gray-400 hover:text-[#E5E5E5]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="text-center space-y-4">
                <div className="bg-black/30 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Үндсэн дүн:</span>
                    <span className="text-[#E5E5E5]">₮{qpayData.original_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-[#22C55E]">
                    <span>Хөнгөлөлт ({qpayData.discount_percentage}%):</span>
                    <span>-₮{qpayData.discount_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-2">
                    <span className="text-[#E5E5E5] font-bold">Төлөх дүн:</span>
                    <span className="text-[#22C55E] font-bold text-xl">₮{qpayData.final_amount?.toLocaleString()}</span>
                  </div>
                </div>

                {qpayData.qr_code && (
                  <div className="bg-white p-4 rounded-lg inline-block">
                    <img src={qpayData.qr_code} alt="QPay QR" className="w-48 h-48" />
                  </div>
                )}

                <p className="text-sm text-gray-400">
                  Банкны апп-аар QR код уншуулна уу
                </p>

                <Button
                  onClick={checkQPayPayment}
                  disabled={checkingPayment}
                  className="w-full btn-primary py-4"
                >
                  {checkingPayment ? 'Шалгаж байна...' : 'Төлбөр шалгах'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Shift Modal */}
        {/* Shelly Config Modal */}
        {showShellyConfig && shellyConfigTable && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="glass-card p-6 max-w-md w-full space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#E5E5E5]" style={{fontFamily: 'Oswald'}}>
                  <Lightbulb className="w-6 h-6 inline-block mr-2 text-yellow-500" />
                  SHELLY ТОХИРГОО
                </h2>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowShellyConfig(false)}
                  className="text-gray-400 hover:text-[#E5E5E5]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="bg-black/30 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#22C55E]" style={{fontFamily: 'Oswald'}}>
                  {shellyConfigTable.table_code}
                </p>
                <p className="text-sm text-gray-400">{shellyConfigTable.table_type}</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Shelly Device ID (Cloud)</Label>
                  <Input
                    value={shellyDeviceId}
                    onChange={(e) => setShellyDeviceId(e.target.value)}
                    placeholder="Жнь: 98CDAC123456"
                    className="bg-black/50 border-white/10 text-[#E5E5E5]"
                  />
                  <p className="text-xs text-gray-500">Shelly App → Device → Settings → Device Info → Device ID</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-400">Shelly IP хаяг (Local)</Label>
                  <Input
                    value={shellyIp}
                    onChange={(e) => setShellyIp(e.target.value)}
                    placeholder="Жнь: 192.168.1.100"
                    className="bg-black/50 border-white/10 text-[#E5E5E5]"
                  />
                  <p className="text-xs text-gray-500">Дотоод сүлжээнд ашиглагдана</p>
                </div>
              </div>

              {/* Test buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => testShellyLight(true)}
                  disabled={shellyTesting || (!shellyDeviceId && !shellyIp)}
                  className="bg-green-500/20 border border-green-500/30 hover:bg-green-500/30 text-green-400"
                >
                  <Lightbulb className="w-4 h-4 mr-2" />
                  {shellyTesting ? 'Туршиж байна...' : 'АСААХ тест'}
                </Button>
                <Button
                  onClick={() => testShellyLight(false)}
                  disabled={shellyTesting || (!shellyDeviceId && !shellyIp)}
                  className="bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 text-red-400"
                >
                  <Lightbulb className="w-4 h-4 mr-2" />
                  {shellyTesting ? 'Туршиж байна...' : 'УНТРААХ тест'}
                </Button>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowShellyConfig(false)}
                  variant="outline"
                  className="flex-1 bg-white/10 border-white/10 hover:bg-white/20"
                >
                  Болих
                </Button>
                <Button
                  onClick={saveShellyConfig}
                  className="flex-1 btn-primary"
                >
                  Хадгалах
                </Button>
              </div>

              {shellyConfigTable.shelly_device_id && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                  <p className="text-green-400 text-sm">✓ Cloud тохиргоотой</p>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Order Detail Modal */}
        {showOrderDetail && selectedOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="glass-card p-6 max-w-lg w-full space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#E5E5E5]" style={{fontFamily: 'Oswald'}}>
                  ЗАХИАЛГЫН ДЭЛГЭРЭНГҮЙ
                </h2>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowOrderDetail(false)}
                  className="text-gray-400 hover:text-[#E5E5E5]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Order Header */}
              <div className="bg-black/30 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Ширээ:</span>
                  <span className="text-[#E5E5E5] font-bold text-xl">Ширээ {selectedOrder.table_number}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Төлөв:</span>
                  <span className={`px-3 py-1 rounded text-sm font-bold ${
                    selectedOrder.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                    selectedOrder.status === 'preparing' ? 'bg-blue-500/20 text-blue-500' :
                    'bg-green-500/20 text-green-500'
                  }`}>
                    {selectedOrder.status === 'pending' ? 'Хүлээгдэж байна' : 
                     selectedOrder.status === 'preparing' ? 'Бэлтгэж байна' : 'Хүргэгдсэн'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Захиалсан:</span>
                  <span className="text-[#E5E5E5]">
                    {new Date(selectedOrder.created_at).toLocaleString('mn-MN', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      day: '2-digit',
                      month: '2-digit'
                    })}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-[#E5E5E5]">Захиалгын жагсаалт</h3>
                <div className="bg-black/30 rounded-lg divide-y divide-white/10">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3">
                      <div>
                        <span className="text-[#E5E5E5] font-medium">{item.name}</span>
                        <span className="text-gray-400 ml-2">x{item.quantity}</span>
                      </div>
                      <span className="text-[#22C55E] font-semibold">
                        ₮{((item.price || 0) * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="bg-[#22C55E]/10 rounded-lg p-4 border border-[#22C55E]/30">
                <div className="flex justify-between items-center">
                  <span className="text-[#E5E5E5] font-bold text-lg">НИЙТ ДҮН:</span>
                  <span className="text-[#22C55E] font-bold text-2xl" style={{fontFamily: 'Oswald'}}>
                    ₮{(selectedOrder.total || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Status Update */}
              {selectedOrder.status !== 'completed' && (
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => {
                      updateOrderStatus(selectedOrder.id, 'pending');
                      setSelectedOrder({...selectedOrder, status: 'pending'});
                    }}
                    variant="outline"
                    className={`${selectedOrder.status === 'pending' ? 'bg-yellow-500/20 border-yellow-500' : 'bg-white/5 border-white/10'}`}
                  >
                    Хүлээгдэж
                  </Button>
                  <Button
                    onClick={() => {
                      updateOrderStatus(selectedOrder.id, 'preparing');
                      setSelectedOrder({...selectedOrder, status: 'preparing'});
                    }}
                    variant="outline"
                    className={`${selectedOrder.status === 'preparing' ? 'bg-blue-500/20 border-blue-500' : 'bg-white/5 border-white/10'}`}
                  >
                    Бэлтгэж
                  </Button>
                  <Button
                    onClick={() => {
                      updateOrderStatus(selectedOrder.id, 'completed');
                      setSelectedOrder({...selectedOrder, status: 'completed'});
                    }}
                    variant="outline"
                    className="bg-green-500/20 border-green-500/30 hover:bg-green-500/30"
                  >
                    Хүргэгдсэн
                  </Button>
                </div>
              )}

              <Button
                onClick={() => setShowOrderDetail(false)}
                variant="outline"
                className="w-full bg-white/10 border-white/10 hover:bg-white/20"
              >
                Хаах
              </Button>
            </Card>
          </div>
        )}

        {/* Two Column Layout: Orders + Finished Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders - First Column */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#E5E5E5]" style={{fontFamily: 'Oswald'}}>
              Сүүлийн Захиалгууд
            </h2>
            <div data-testid="orders-list" className="space-y-3 max-h-[500px] overflow-y-auto">
              {/* Active orders first (pending, preparing), then completed */}
              {orders
                .sort((a, b) => {
                  const order = { pending: 0, preparing: 1, completed: 2 };
                  return (order[a.status] || 0) - (order[b.status] || 0);
                })
                .slice(0, 15)
                .map(order => (
                <Card 
                  key={order.id} 
                  className={`glass-card p-4 cursor-pointer hover:border-white/30 transition-all ${order.status === 'completed' ? 'opacity-60' : ''}`}
                  onClick={() => {
                    setSelectedOrder(order);
                    setShowOrderDetail(true);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-[#E5E5E5]" style={{fontFamily: 'Oswald'}}>
                          Ширээ {order.table_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                          order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                          order.status === 'preparing' ? 'bg-blue-500/20 text-blue-500' :
                          'bg-green-500/20 text-green-500'
                        }`}>
                          {order.status === 'pending' ? 'Хүлээгдэж байна' : 
                           order.status === 'preparing' ? 'Бэлтгэж байна' : 'Хүргэгдсэн'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        {order.items?.map(item => `${item.name} x${item.quantity}`).join(', ')}
                      </div>
                    </div>
                    <div className="text-right flex flex-col gap-1">
                      <div className="text-lg font-bold text-[#22C55E]">
                        ₮{(order.total || 0).toLocaleString()}
                      </div>
                      {order.status !== 'completed' && (
                        <select
                          value={order.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateOrderStatus(order.id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="bg-black/50 border border-white/10 text-[#E5E5E5] text-xs rounded px-2 py-1"
                        >
                          <option value="pending">Хүлээгдэж байна</option>
                          <option value="preparing">Бэлтгэж байна</option>
                          <option value="completed">Хүргэгдсэн</option>
                        </select>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              {orders.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Захиалга байхгүй
                </div>
              )}
            </div>
          </div>

          {/* Finished Tables - Second Column - Needs Payment */}
          <div className="space-y-4">
            <div className="section-header">
              <div className="indicator" style={{ backgroundColor: '#F59E0B' }}></div>
              <h2 className="text-lg font-semibold text-[#E5E5E5]">
                Finished Sessions
              </h2>
            </div>
            <div data-testid="finished-sessions-list" className="space-y-3 max-h-[500px] overflow-y-auto">
              {/* Unpaid first, then partial, then paid */}
              {finishedSessions
                .sort((a, b) => {
                  const order = { unpaid: 0, partial: 1, paid: 2 };
                  return (order[a.payment_status] || 0) - (order[b.payment_status] || 0);
                })
                .map(session => (
                <Card 
                  key={session.id} 
                  className={`bg-[#141414] border-[#262626] p-4 rounded-xl hover-lift ${session.payment_status === 'paid' ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-[#E5E5E5]">
                          {session.table_code}
                        </span>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                          session.payment_status === 'paid' ? 'bg-[#22C55E]/15 text-[#22C55E]' :
                          session.payment_status === 'partial' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' :
                          'bg-[#DC2626]/15 text-[#DC2626]'
                        }`}>
                          {session.payment_status === 'paid' ? 'Paid' :
                           session.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                        </span>
                      </div>
                      <div className="text-sm text-[#525252]">
                        {session.duration_minutes ? `${Math.round(session.duration_minutes)} min` : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-[#22C55E]">
                        ₮{(session.total_amount || 0).toLocaleString()}
                      </div>
                      {session.paid_amount > 0 && session.payment_status !== 'paid' && (
                        <div className="text-xs text-[#525252]">
                          Remaining: ₮{((session.total_amount || 0) - (session.paid_amount || 0)).toLocaleString()}
                        </div>
                      )}
                      {session.payment_status !== 'paid' && (
                        <Button
                          onClick={() => openPaymentModal(session)}
                          size="sm"
                          className="mt-2 bg-[#D97706] hover:bg-[#4DA3FF] text-[#E5E5E5] shadow-lg shadow-[#D97706]/20"
                        >
                          Pay
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              {finishedSessions.length === 0 && (
                <div className="text-center py-12 text-[#525252]">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No finished sessions</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ClubOS Wrapper component for /admin/new route
const ClubOSDashboard = () => {
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('admin_role');
    if (role === 'barmen' && token) {
      try {
        const res = await axios.post(`${API}/shifts/barmen-logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Open bills never block logout: the shift continues and the next
        // barmen logs in to it. Only «Ээлж хаах» requires every bill closed.
        if (res.data?.work_duration) {
          toast.success(`Ажилласан: ${res.data.work_duration}`);
        }
      } catch (err) { /* allow logout */ }
    }
    clearSession();
    window.location.href = '/';
  };

  // Check auth - only run once on mount
  React.useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <CloudAccountGate onLogout={handleLogout}><FeedbackProvider><ClubOSApp onLogout={handleLogout} navigate={navigate} /></FeedbackProvider></CloudAccountGate>;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <div className="App">
          <LocalPosBadge />
          <CloudSyncStatusBadge />
          <LocalUpdateNotice />
          <ClubProvider>
            <Toaster position="top-center" />
            <BrowserRouter>
              <Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/pos/activate" element={<PosActivationPage />} />
                  <Route path="/menu/:spaceId" element={<QRMenuPage />} />
                  <Route path="/qr/space/:spaceId" element={<ToGuestMenu />} />
                  <Route path="/qr/space/:spaceId/menu" element={<QRMenuPage />} />
                  <Route path="/member" element={<MemberPortal />} />
                  <Route path="/table/:tableId" element={<ToGuestMenu />} />
                  <Route path="/table/:tableId/menu" element={<ToGuestMenu />} />
                  <Route path="/admin" element={<Navigate to="/" replace />} />
                  <Route path="/admin/dashboard" element={<ClubOSDashboard />} />
                  <Route path="/admin/feedback" element={<ClubOSDashboard />} />
                  <Route path="/admin/feedback/:feedbackId" element={<ClubOSDashboard />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ClubProvider>
        </div>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
